import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { toast } from 'sonner'
import { Plus, User, Users, Building, Trash2, AlertCircle } from 'lucide-react'
import type {
  MilestoneAssignment,
  MilestoneAssignmentFormData,
  MilestoneAssignmentWithRelations,
  AssigneeType
} from '@/types/milestone'

interface MilestoneAssignmentsProps {
  milestoneId: string
  projectId: string
  totalEquity?: number // Total shares or percentage for this milestone
  isPercentageBased?: boolean
}

export default function MilestoneAssignments({
  milestoneId,
  projectId,
  totalEquity = 0,
  isPercentageBased = false
}: MilestoneAssignmentsProps) {
  const [showAddDialog, setShowAddDialog] = useState(false)
  const queryClient = useQueryClient()

  // Form state
  const [formData, setFormData] = useState<MilestoneAssignmentFormData>({
    assignee_type: 'user',
    assignee_id: undefined,
    allocation_percentage: 0,
    vesting_schedule_id: undefined
  })

  // Fetch assignments
  const { data: assignments, isLoading } = useQuery({
    queryKey: ['milestone-assignments', milestoneId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('milestone_assignments')
        .select(`
          *,
          assignee:assignee_id (
            id,
            first_name,
            last_name,
            email
          ),
          vesting_schedule:vesting_schedule_id (
            id,
            schedule_name,
            cliff_months,
            duration_months
          )
        `)
        .eq('milestone_id', milestoneId)
        .order('created_at', { ascending: true })

      if (error) throw error
      return (data || []) as unknown as MilestoneAssignmentWithRelations[]
    }
  })

  // Fetch users for assignment
  const { data: users } = useQuery({
    queryKey: ['project-users', projectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('project_members')
        .select(`
          user_id,
          users!project_members_user_id_fkey (
            id,
            first_name,
            last_name,
            email
          )
        `)
        .eq('project_id', projectId)

      if (error) throw error
      return (data?.map(d => (d as any).users).filter(Boolean) || []) as { id: string; first_name: string | null; last_name: string | null; email: string }[]
    }
  })

  // Fetch vesting schedules
  const { data: vestingSchedules } = useQuery({
    queryKey: ['vesting-schedules', projectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('vesting_schedules')
        .select('*')
        .eq('project_id', projectId)

      if (error) throw error
      return data || []
    }
  })

  // Calculate total allocated percentage
  const totalAllocated = assignments?.reduce((sum: number, a: MilestoneAssignmentWithRelations) => sum + a.allocation_percentage, 0) || 0
  const remainingAllocation = 100 - totalAllocated

  // Create assignment mutation
  const { mutate: createAssignment, isPending: isCreating } = useMutation({
    mutationFn: async (data: MilestoneAssignmentFormData) => {
      const { error } = await supabase
        .from('milestone_assignments')
        .insert({
          milestone_id: milestoneId,
          ...data
        })

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['milestone-assignments', milestoneId] })
      setShowAddDialog(false)
      resetForm()
      toast.success('Assignment added')
    },
    onError: (error: Error) => {
      toast.error(error.message)
    }
  })

  // Delete assignment mutation
  const { mutate: deleteAssignment } = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('milestone_assignments')
        .delete()
        .eq('id', id)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['milestone-assignments', milestoneId] })
      toast.success('Assignment removed')
    },
    onError: (error: Error) => {
      toast.error(error.message)
    }
  })

  const resetForm = () => {
    setFormData({
      assignee_type: 'user',
      assignee_id: undefined,
      allocation_percentage: 0,
      vesting_schedule_id: undefined
    })
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    // Validate allocation doesn't exceed 100%
    if (totalAllocated + formData.allocation_percentage > 100) {
      toast.error(`Cannot allocate more than 100%. Available: ${remainingAllocation}%`)
      return
    }

    createAssignment(formData)
  }

  const getAssigneeIcon = (type: AssigneeType) => {
    switch (type) {
      case 'user': return User
      case 'team': return Users
      case 'all_employees': return Building
      case 'specific_role': return Users
      default: return User
    }
  }

  const getAssigneeLabel = (assignment: MilestoneAssignmentWithRelations) => {
    switch (assignment.assignee_type) {
      case 'user':
        return assignment.assignee
          ? `${assignment.assignee.first_name} ${assignment.assignee.last_name}`
          : 'Unknown User'
      case 'all_employees':
        return 'All Employees'
      case 'specific_role':
        return `Role: ${assignment.role || 'Unknown'}`
      case 'team':
        return `Team: ${assignment.team_id || 'Unknown'}`
      default:
        return 'Unknown'
    }
  }

  const calculateEquityAmount = (percentage: number) => {
    if (!totalEquity) return 'N/A'
    const amount = (totalEquity * percentage) / 100

    if (isPercentageBased) {
      return `${(amount).toFixed(3)}%`
    } else {
      return `${Math.round(amount).toLocaleString()} shares`
    }
  }

  if (isLoading) {
    return <div className="animate-pulse h-32 bg-gray-100 rounded-lg" />
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-sm font-medium text-gray-700">Equity Assignments</h3>
        <Button
          size="sm"
          onClick={() => setShowAddDialog(true)}
          disabled={remainingAllocation <= 0}
        >
          <Plus className="h-4 w-4 mr-1" />
          Add Assignment
        </Button>
      </div>

      {/* Allocation Progress */}
      <div className="bg-gray-50 p-3 rounded-lg">
        <div className="flex justify-between text-sm mb-2">
          <span className="text-gray-600">Total Allocated</span>
          <span className="font-medium">{totalAllocated}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all ${
              totalAllocated === 100 ? 'bg-green-600' : 'bg-blue-600'
            }`}
            style={{ width: `${totalAllocated}%` }}
          />
        </div>
        {totalAllocated < 100 && (
          <p className="text-xs text-gray-500 mt-1">
            {remainingAllocation}% available for assignment
          </p>
        )}
      </div>

      {/* Assignments List */}
      {assignments && assignments.length > 0 ? (
        <div className="space-y-2">
          {assignments.map((assignment: MilestoneAssignmentWithRelations) => {
            const Icon = getAssigneeIcon(assignment.assignee_type)

            return (
              <div
                key={assignment.id}
                className="flex items-center justify-between p-3 bg-white border rounded-lg"
              >
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-gray-100 rounded-full">
                    <Icon className="h-4 w-4 text-gray-600" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">{getAssigneeLabel(assignment)}</p>
                    <div className="flex items-center space-x-2 text-xs text-gray-500">
                      <span>{assignment.allocation_percentage}%</span>
                      <span>-</span>
                      <span>{calculateEquityAmount(assignment.allocation_percentage)}</span>
                      {assignment.vesting_schedule && (
                        <>
                          <span>-</span>
                          <span>{assignment.vesting_schedule.schedule_name}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => deleteAssignment(assignment.id)}
                  className="text-red-600 hover:text-red-900"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            )
          })}
        </div>
      ) : (
        <div className="text-center py-6 bg-gray-50 rounded-lg">
          <Users className="h-8 w-8 text-gray-400 mx-auto mb-2" />
          <p className="text-sm text-gray-500">No assignments yet</p>
          <p className="text-xs text-gray-400">Add assignments to distribute equity when milestone is achieved</p>
        </div>
      )}

      {/* Add Assignment Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <form onSubmit={handleSubmit}>
            <DialogHeader>
              <DialogTitle>Add Assignment</DialogTitle>
              <DialogDescription>
                Assign equity allocation for this milestone
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="assignee_type">Assignment Type</Label>
                <Select
                  value={formData.assignee_type}
                  onValueChange={(value) => {
                    setFormData({
                      ...formData,
                      assignee_type: value as AssigneeType,
                      assignee_id: undefined,
                      role: undefined
                    })
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">Specific User</SelectItem>
                    <SelectItem value="all_employees">All Employees</SelectItem>
                    <SelectItem value="specific_role">Specific Role</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {formData.assignee_type === 'user' && (
                <div className="space-y-2">
                  <Label htmlFor="assignee_id">Select User</Label>
                  <Select
                    value={formData.assignee_id || ''}
                    onValueChange={(value) => setFormData({ ...formData, assignee_id: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a user" />
                    </SelectTrigger>
                    <SelectContent>
                      {users?.map((user) => (
                        <SelectItem key={user.id} value={user.id}>
                          {user.first_name} {user.last_name} ({user.email})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {formData.assignee_type === 'specific_role' && (
                <div className="space-y-2">
                  <Label htmlFor="role">Role Name</Label>
                  <Input
                    id="role"
                    value={formData.role || ''}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    placeholder="e.g., Engineering, Sales"
                    required
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="allocation">Allocation Percentage</Label>
                <Input
                  id="allocation"
                  type="number"
                  min="0.01"
                  max={remainingAllocation}
                  step="0.01"
                  value={formData.allocation_percentage || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    allocation_percentage: parseFloat(e.target.value) || 0
                  })}
                  placeholder="0"
                  required
                />
                <p className="text-xs text-gray-500">
                  {remainingAllocation}% available -
                  Equals {calculateEquityAmount(formData.allocation_percentage || 0)}
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="vesting">Vesting Schedule (Optional)</Label>
                <Select
                  value={formData.vesting_schedule_id || ''}
                  onValueChange={(value) => setFormData({
                    ...formData,
                    vesting_schedule_id: value || undefined
                  })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select vesting schedule" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">No vesting</SelectItem>
                    {vestingSchedules?.map((schedule) => (
                      <SelectItem key={schedule.id} value={schedule.id}>
                        {schedule.schedule_name} ({schedule.cliff_months || 0}m cliff, {schedule.duration_months}m total)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {formData.assignee_type === 'all_employees' && (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Equity will be distributed proportionally among all employees
                    when the milestone is achieved
                  </AlertDescription>
                </Alert>
              )}
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowAddDialog(false)
                  resetForm()
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={
                  isCreating ||
                  !formData.allocation_percentage ||
                  (formData.assignee_type === 'user' && !formData.assignee_id) ||
                  (formData.assignee_type === 'specific_role' && !formData.role)
                }
              >
                {isCreating ? 'Adding...' : 'Add Assignment'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}
